import React, { useEffect, useState } from "react";
import { benefitsBg } from "../../assets";
import { useLanguage } from "../../contexts/LanguageContext";
import { GetAxios } from "../../helpers/axiosHelpers";
import { resolveIcon } from "../../helpers/iconResolver";

const DefaultCardIcon = () => (
  <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="20" cy="8.26215" rx="10.2783" ry="4.26215" stroke="#367FAC" strokeWidth="2.3"/>
    <path d="M9.72168 12.5243C9.72168 12.5243 9.72168 16.2398 9.72168 19.0324C9.72168 21.8251 15.6317 24.065 20.2785 24.065C24.9255 24.065 30.8357 21.8251 30.8357 19.0324C30.8357 17.6388 30.8357 12.5243 30.8357 12.5243" stroke="#367FAC" strokeWidth="2.3" strokeLinecap="square"/>
    <path d="M9.72168 4.26215C9.72168 4.26215 9.72168 9.46936 9.72168 12.0324C9.72168 14.5955 15.6317 16.2946 20.2785 16.2946C24.9255 16.2946 30.8357 14.5955 30.8357 12.0324C30.8357 10.6388 30.8357 4.26215 30.8357 4.26215" stroke="#367FAC" strokeWidth="2.3"/>
  </svg>
);

const BenefitSection = ({ sectionData }) => {
  const { t, language } = useLanguage();
  const pick = (en, ja, key) => (language === "ja" && ja) ? ja : (en ?? t(key));
  const [cards, setCards] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    GetAxios("/service-cards/public?section=benefits")
      .then((res) => {
        if (res?.success && Array.isArray(res.data) && res.data.length > 0) {
          setCards(res.data);
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const localeServices = t("benefits.services");
  const services = cards.length > 0
    ? cards.map((c) => ({
        title: (language === "ja" && c.titleJa) ? c.titleJa : c.titleEn,
        description: (language === "ja" && c.descJa) ? c.descJa : c.descEn,
        iconName: c.iconName,
      }))
    : Array.isArray(localeServices) ? localeServices : [];

  if (!loaded && services.length === 0) return null;

  return (
    <section
      id="benefits"
      className="py-14 bg-cover bg-center"
      style={{ backgroundImage: `url(${benefitsBg})`, backgroundColor: "#292F3A" }}
    >
      <div className="max-w-[1280px] mx-auto px-8">
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1 h-4 bg-accent-green rounded-full"></div>
            <span className="text-[10px] font-black text-accent-green uppercase tracking-[0.25em]">
              {pick(sectionData?.labelEn, sectionData?.labelJa, "benefits.sectionLabel")}
            </span>
          </div>
          <h2 className="text-white text-3xl font-semibold leading-tight mb-3 tracking-tight max-w-2xl">
            {pick(sectionData?.titleEn, sectionData?.titleJa, "benefits.title")}
          </h2>
          <p className="text-[#C7C7C7] text-base leading-relaxed max-w-2xl">
            {pick(sectionData?.subtitleEn, sectionData?.subtitleJa, "benefits.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {services.map((service, idx) => {
            const IconComp = service.iconName ? resolveIcon(service.iconName) : null;

            return (
              <div
                key={idx}
                className="bg-[#292F3A] border border-[#21252C] rounded-xl p-5 hover:border-[#367FAC]/40 transition-all duration-300 group hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-[#21252C] rounded-lg p-3 inline-block group-hover:bg-[#367FAC]/20 transition-colors">
                    {IconComp ? (
                      <IconComp className="w-8 h-8 text-[#367FAC]" />
                    ) : (
                      <DefaultCardIcon />
                    )}
                  </div>
                  <span className="text-[10px] font-black text-[#367FAC]/50 group-hover:text-[#367FAC] tracking-widest transition-colors">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="text-white text-sm font-semibold mb-2 leading-snug">
                  {service.title}
                </h3>
                <p className="text-[#B4BBC5] text-xs leading-relaxed">
                  {service.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BenefitSection;
